import React, { Component, ComponentType } from 'react';
import { withTranslation, WithTranslation } from 'react-i18next';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { compose } from 'redux';
import { connect, ConnectedProps } from 'react-redux';
import { UnregisterCallback } from 'history';
import { dialogsService } from '@aitex/tsx-extranet-dialogs';
import { RootState } from '../../store';
import { IWebinarDetailContainerStateProps, IWebinarDetailContainerDispatchProps } from '../../interfaces/props/IWebinarDetailProps';
import { IWebinarDetailContainerRouteParams } from '../../interfaces/routeParams/IWebinarDetailRouteParams';
import * as webinarDetailStore from '../../store/modules/webinarDetail.store';
import { IWebinar } from '../../common/model/webinar.model';
import { DialogId } from '../../common/model/enumerations/dialogId.model';
import { webinarsService } from '../../services';
import { notifySuccess, notifyError } from '../../utils/toast.utils';
import InfoCard from './infoCard/infoCard';
import './webinarDetail.container.scss';

type WebinarDetailContainerPropsType = PropsFromRedux & RouteComponentProps<IWebinarDetailContainerRouteParams> & WithTranslation;

type WebinarDetailContainerStateType = IWebinarDetailContainerState;

class WebinarDetailContainer extends Component<WebinarDetailContainerPropsType, WebinarDetailContainerStateType> {
  private unregisterCallback: UnregisterCallback = null;

  private initialState: WebinarDetailContainerStateType = {
    isSaving: false
  };

  public constructor(props: WebinarDetailContainerPropsType) {
    super(props);

    props.reset();

    this.state = this.initialState;
  }

  public componentDidMount() {
    this.init();
  }

  public componentDidUpdate(prevProps: WebinarDetailContainerPropsType): void {
    if (!this.props.isReadyForRendering && prevProps.isReadyForRendering) {
      this.init();
    }

    if (this.props.isReadyForRendering && prevProps.location.pathname !== this.props.location.pathname) {
      const isView = this.computeIsView();
      const isNew = this.computeIsNew();
      if (isView !== this.props.isView) {
        this.props.setIsView(isView);
      }
      if (!isNew && this.props.isNew) {
        this.props.setIsView(false);
      }
      if ((isNew && !this.props.isNew) || (this.props.webinar.id && this.props.webinar.id !== this.props.match.params.id)) {
        this.props.reset();
      }
    }
  }

  public componentWillUnmount(): void {
    const { isReadyForRendering, reset } = this.props;

    if (isReadyForRendering) {
      reset();
    }
  }

  public edit(): void {
    this.props.history.replace(this.props.location.pathname.replace('/view', '/edit'));
  }

  public async save(): Promise<void> {
    const { webinar, i18n } = this.props;

    if (!this.validate()) {
      return;
    }

    this.setState({ isSaving: true });
    await this.saveCommon(webinar, i18n.t('webinarDetail.messages.saveSuccess'), i18n.t('webinarDetail.errors.cannotSaveWebinar'));
    this.setState({ isSaving: false });
  }

  public cancel(): void {
    this.props.history.push('/webinars-list');
  }

  public render(): JSX.Element {
    const {
      isReadyForRendering,
      webinar,
      isView,
      isNew,
      setWebinarName,
      setWebinarSpeakers,
      setWebinarDate,
      setWebinarDescription,
      setWebinarVideoUrl,
      setWebinarVideo,
      setWebinarActive,
      i18n,
      errors,
      setErrors
    } = this.props;

    if (!isReadyForRendering) {
      return null;
    }

    if (!isNew && !webinar.id) {
      return null; // TODO: loading
    }

    const title = isNew ? <h2 className='titleWebinar'>{i18n.t('webinarDetail.titleNew')}</h2> : <h2 className='titleWebinar'>{i18n.t('webinarDetail.title')}</h2>;

    return (
      <div>
        <div className='clearfix'>
          {title}
        </div>
        <InfoCard
          webinar={webinar}
          isView={isView}
          isNew={isNew}
          isSaving={this.state.isSaving}
          onEditButtonClick={(): void => this.edit()}
          onSaveButtonClick={(): Promise<void> => this.save()}
          onCancelButtonClick={(): void => this.cancel()}
          onNameChange={setWebinarName}
          onSpeakersChange={setWebinarSpeakers}
          onDateChange={setWebinarDate}
          onDescriptionChange={setWebinarDescription}
          onVideoUrlChange={setWebinarVideoUrl}
          onVideoChange={setWebinarVideo}
          onActiveChange={setWebinarActive}
          errors={errors}
          setErrors={setErrors}
        />
      </div>
    );
  }

  private computeIsView(): boolean {
    return this.props.location.pathname.includes('/view');
  }

  private computeIsNew(): boolean {
    return this.props.location.pathname.includes('/new');
  }

  private init(): void {
    const { makeReadyForRendering, fetchWebinar, match } = this.props;

    const isView = this.computeIsView();
    const isNew = this.computeIsNew();
    makeReadyForRendering(isView, isNew);
    if (!isNew) {
      fetchWebinar(match.params.id, webinarsService);
    }
  }

  private async saveCommon(webinar: IWebinar, successMessage: string, errorMessage: string): Promise<void> {
    await dialogsService.openDialog(DialogId.GENERIC_CONFIRMATION)
      .then(async () => {
        try {

          const savedWebinar = webinar.id ? await webinarsService.update(webinar) : await webinarsService.create(webinar);
          notifySuccess(successMessage);

          this.props.saveWebinar(savedWebinar);

          if (this.unregisterCallback) {
            this.unregisterCallback();
          }
          this.cancel();
        } catch (error) {
          notifyError(errorMessage);
        } finally {
          this.setState({ isSaving: false });
        }
      })
      .catch(() => {
        // DO nothing
      });
  }

  private validate(): boolean {
    const { webinar, i18n, isNew } = this.props;

    if (webinar.name === undefined || webinar.name === null || webinar.name.trim() === '') {
      notifyError(i18n.t('webinarDetail.errors.nameRequired'));
      return false;
    }
    if (webinar.speakers === undefined || webinar.speakers === null || webinar.speakers.trim() === '') {
      notifyError(i18n.t('webinarDetail.errors.speakersRequired'));
      return false;
    }
    if (!webinar.date) {
      notifyError(i18n.t('webinarDetail.errors.dateRequired'));
      return false;
    }
    if (webinar.description === undefined || webinar.description === null || webinar.description.trim() === '') {
      notifyError(i18n.t('webinarDetail.errors.descriptionRequired'));
      return false;
    }

    if (isNew) {
      if (webinar.videoUrl === undefined || webinar.videoUrl === null) {
        notifyError(i18n.t('webinarDetail.errors.videoUrlRequired'));
        return false;
      }
    }

    return true;
  }
}

const mapStateToProps = (state: RootState): IWebinarDetailContainerStateProps => ({
  isReadyForRendering: state.webinarDetailStore.isReadyForRendering,
  webinar: state.webinarDetailStore.webinar,
  webinarInitial: state.webinarDetailStore.webinarInitial,
  isView: state.webinarDetailStore.isView,
  isNew: state.webinarDetailStore.isNew,
  errors: state.webinarDetailStore.errors
});

const mapDispatchToProps: IWebinarDetailContainerDispatchProps = {
  reset: webinarDetailStore.reset,
  makeReadyForRendering: webinarDetailStore.makeReadyForRendering,
  setIsView: webinarDetailStore.setIsView,
  setWebinarName: webinarDetailStore.setWebinarName,
  setWebinarSpeakers: webinarDetailStore.setWebinarSpeakers,
  setWebinarDate: webinarDetailStore.setWebinarDate,
  setWebinarDescription: webinarDetailStore.setWebinarDescription,
  setWebinarVideoUrl: webinarDetailStore.setWebinarVideoUrl,
  setWebinarVideo: webinarDetailStore.setWebinarVideo,
  setWebinarActive: webinarDetailStore.setWebinarActive,
  fetchWebinar: webinarDetailStore.fetchWebinar,
  resetWebinar: webinarDetailStore.resetWebinar,
  saveWebinar: webinarDetailStore.saveWebinar,
  setErrors: webinarDetailStore.setErrors
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

interface IWebinarDetailContainerState {
  isSaving: boolean;
}

export default compose(
  withRouter,
  withTranslation(),
  connector,
)(WebinarDetailContainer) as ComponentType;
